import { FilterEventDto } from '@smatchy/sdk';
import { GeoLocality, GeoPosition } from '../domains/locations';
import { isProximitySearchLevel, resolveCountryCodeFromName } from './resolve-admin-level.util';

export type LocationSearchStateInput = {
  radius?: number;
  /*
   * `| null` because the states that pass themselves here clear the location
   * with null, not undefined: it is the value written in their defaults and by
   * every reset. Everything below already reads it with `?.`, so accepting the
   * shape the callers actually hold changes nothing here.
   */
  location?: {
    position?: GeoPosition;
    locality?: GeoLocality;
  } | null;
};

const resolveFilterPosition = (
  location?: LocationSearchStateInput['location'],
): { lat: number; lng: number } | undefined => {
  const position = location?.position;
  if (position?.lat === undefined || position?.lat === null) {
    return undefined;
  }
  const lng = position.lng ?? (position as { lon?: number }).lon;
  if (lng === undefined || lng === null) {
    return undefined;
  }
  return { lat: position.lat, lng };
};

const resolveSelectedLevel = (
  level?: GeoLocality['adminLevel'],
): 'city' | 'department' | 'region' | 'country' | undefined => (level && level !== 'other' ? level : undefined);

/**
 * Build location fields for event/group search (radius vs hierarchy).
 */
export const buildLocationSearchFields = (
  state: LocationSearchStateInput,
): Pick<FilterEventDto, 'location' | 'radiusMeter' | 'searchMode' | 'hierarchy'> => {
  const filterPosition = resolveFilterPosition(state.location);
  const locality = state.location?.locality;
  const adminLevel = locality?.adminLevel || 'city';
  const isHierarchySearch = !isProximitySearchLevel(adminLevel);

  if (isHierarchySearch && locality) {
    const hierarchyName =
      adminLevel === 'country'
        ? locality.hierarchy?.country?.name || locality.country || locality.city
        : adminLevel === 'region'
        ? locality.hierarchy?.region?.name || locality.region || locality.city
        : adminLevel === 'department'
        ? locality.hierarchy?.department?.name || locality.region || locality.city
        : locality.hierarchy?.city?.name || locality.city;
    const countryCode =
      locality.countryCode ||
      locality.hierarchy?.country?.code ||
      resolveCountryCodeFromName(
        locality.hierarchy?.country?.name || locality.country || (adminLevel === 'country' ? hierarchyName : undefined),
      ) ||
      undefined;
    return {
      searchMode: 'hierarchy',
      hierarchy: {
        level: adminLevel === 'other' ? 'region' : (adminLevel as 'city' | 'department' | 'region' | 'country'),
        // Country profiles rarely store country.placeId (backfill uses
        // code/name only). Prefer code/name match; keep placeId as OR.
        placeId: adminLevel === 'country' ? undefined : locality.placeId,
        name: hierarchyName,
        countryCode,
      },
      location: filterPosition
        ? {
            lat: filterPosition.lat,
            lon: filterPosition.lng,
            name: locality.formattedAddress ?? '',
            placeId: locality.placeId,
            selectedLevel: resolveSelectedLevel(locality.selectedLevel || locality.adminLevel),
            selectedName: locality.selectedName || hierarchyName || locality.suggestionPrimary,
            hierarchy: locality.hierarchy,
          }
        : undefined,
    };
  }

  return {
    searchMode: 'radius',
    radiusMeter: state.radius,
    location: filterPosition
      ? {
          lat: filterPosition.lat,
          lon: filterPosition.lng,
          name: locality?.formattedAddress ?? '',
          placeId: locality?.placeId,
          selectedLevel: resolveSelectedLevel(locality?.selectedLevel || locality?.adminLevel),
          selectedName: locality?.selectedName || locality?.suggestionPrimary || locality?.city,
          hierarchy: locality?.hierarchy,
        }
      : undefined,
  };
};
